import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import GoalSelection from "./ultrafastresearcher/GoalSelection";
import CustomGoalInput from "./ultrafastresearcher/CustomGoalInput";
import WebsiteInput from "./ultrafastresearcher/WebsiteInput";
import QuestionSelection from "./ultrafastresearcher/QuestionSelection";
// ResearchConfirmation removed - email collection integrated with persona display

// Mobile attention nudge: short sine beep + Android vibrate.
// iOS silently ignores vibrate; both stays quiet if no audio context yet.
function notifyUser({ frequency = 880, vibratePattern = 30, gain = 0.08, durationMs = 250 } = {}) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(vibratePattern); } catch (_) { /* iOS / disallowed */ }
  }
  if (typeof window === 'undefined') return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    g.gain.value = gain;
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start();
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
    osc.stop(ctx.currentTime + (durationMs + 50) / 1000);
    setTimeout(() => ctx.close().catch(() => {}), durationMs + 200);
  } catch (_) { /* audio blocked */ }
}

// First-letter initials of the first 1-2 words. Falls back to "?" for empty input.
function getPersonaInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/[\s-]+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

// Step definitions
const STEPS = {
  GOAL_SELECTION: 'goal_selection',
  CUSTOM_GOAL_INPUT: 'custom_goal_input',
  WEBSITE_INPUT: 'website_input',
  QUESTION_SELECTION: 'question_selection',
  DEEP_CONTEXT_RESEARCH: 'deep_context_research',
  PERSONA_DISPLAY: 'persona_display',
  EMAIL_COLLECTION: 'email_collection',
  SUCCESS: 'success'
};

function UltraFastResearch() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('fastresearch');
  const [step, setStep] = useState(STEPS.GOAL_SELECTION);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [researchId, setResearchId] = useState(null);
  const [personas, setPersonas] = useState(null);
  const [personaMetadata, setPersonaMetadata] = useState(null);
  const [imageCheckCount, setImageCheckCount] = useState(0);
  const [email, setEmail] = useState("");
  const [deepContextComplete, setDeepContextComplete] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [restoringState, setRestoringState] = useState(false);
  const [contextCountdown, setContextCountdown] = useState(15); // 15 seconds — matches actual deep-context API time (~11-13s)
  const [personaCountdown, setPersonaCountdown] = useState(25); // 25 seconds for persona-text generation overlay
  const [deepContextMessageIndex, setDeepContextMessageIndex] = useState(0);
  const [personaMessageIndex, setPersonaMessageIndex] = useState(0);
  const [portraitMessageIndex, setPortraitMessageIndex] = useState(0);
  const [personaRevealStep, setPersonaRevealStep] = useState(0); // 0..5 — staggered reveal once text arrives
  const personaCountdownStartedRef = useRef(false); // Track if countdown has started
  const personasArrivedPingedRef = useRef(false); // Fire the persona-ready ping only once
  const wakeLockRef = useRef(null);
  const [language, setLanguage] = useState('en');


  const getApiBaseURL = () => {
    return process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_BACKEND_URL_PROD
      : process.env.REACT_APP_BACKEND_URL_DEV;
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Global Wake Lock API to prevent screen dimming throughout Fast Research flow
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('Fast Research: Wake Lock acquired (global)');
        }
      } catch (err) {
        console.log('Fast Research: Wake Lock error:', err);
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLockRef.current !== null) {
        try {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
          console.log('Fast Research: Wake Lock released');
        } catch (err) {
          console.log('Fast Research: Wake Lock release error:', err);
        }
      }
    };

    // Acquire wake lock on component mount (entering Fast Research)
    requestWakeLock();

    // Also prevent screen timeout with periodic activity as fallback
    const intervalId = setInterval(() => {
      if (document.hidden === false) {
        // Trigger minor DOM update to keep browser active
        document.body.style.opacity = document.body.style.opacity === '1' ? '0.99999' : '1';
      }
    }, 5000); // Every 5 seconds (more frequent for aggressive screen timeouts)

    // Cleanup on unmount (leaving Fast Research)
    return () => {
      clearInterval(intervalId);
      releaseWakeLock();
    };
  }, []); // Empty deps - only run on mount/unmount

  // Handle visibility change to re-acquire wake lock when returning to tab
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && wakeLockRef.current === null) {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            console.log('Fast Research: Wake Lock re-acquired after tab focus');
          }
        } catch (err) {
          console.log('Fast Research: Wake Lock re-acquire error:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also handle focus event for better mobile support
    const handleFocus = async () => {
      if (wakeLockRef.current === null) {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            console.log('Fast Research: Wake Lock re-acquired on focus');
          }
        } catch (err) {
          console.log('Fast Research: Wake Lock focus error:', err);
        }
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Restore state from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const lngParam = params.get('lng');
    if (lngParam) {
      setLanguage(lngParam);
      i18n.changeLanguage(lngParam);
    } else {
      // No explicit language param — default to English
      setLanguage('en');
      i18n.changeLanguage('en');
    }

    if (id) {
      console.log('[FastResearch] Found research ID in URL:', id);
      setRestoringState(true);

      axios.get(`${getApiBaseURL()}/fast-research/${id}`)
        .then(response => {
          if (response.data.success) {
            const research = response.data.research;
            console.log('[FastResearch] Restored research data:', research);

            // Restore all state
            setResearchId(research._id);
            setSelectedGoal(research.goal);
            setWebsiteUrl(research.websiteUrl);

            if (research.websiteSummary) {
              setScanResult({
                websiteUrl: research.websiteUrl,
                summary: research.websiteSummary,
                suggestedQuestions: research.suggestedQuestions,
                scrapedContent: research.scrapedContent,
                screenshotUrl: research.screenshotUrl,
                screenshotS3Key: research.screenshotS3Key,
                researchId: research._id
              });
            }

            if (research.personas && research.personas.length > 0) {
              setPersonas(research.personas);
            }

            // Restore to correct step
            const stepMap = {
              1: STEPS.GOAL_SELECTION,
              2: STEPS.WEBSITE_INPUT,
              3: STEPS.QUESTION_SELECTION,
              4: STEPS.PERSONA_DISPLAY,
              5: STEPS.CONFIRMATION,
              6: STEPS.EMAIL_INPUT,
              7: STEPS.SUCCESS
            };

            let restoredStep = stepMap[research.currentStep] || STEPS.GOAL_SELECTION;

            // Scan done but no goal selected yet — restore to QUESTION_SELECTION so
            // the user can pick from the already-generated suggested questions instead
            // of re-running the scan.
            if (research.currentStep === 2 && research.suggestedQuestions && research.suggestedQuestions.length > 0) {
              console.log('[FastResearch] Step 2 + scan complete — restoring to QUESTION_SELECTION');
              restoredStep = STEPS.QUESTION_SELECTION;
            }

            // Smart restoration: If status indicates personas were attempted/generated
            if (research.status === 'personas_generated' && research.personas && research.personas.length > 0) {
              console.log('[FastResearch] Personas already generated, advancing to persona display');
              restoredStep = STEPS.PERSONA_DISPLAY;
              // If any persona is missing an imageUrl (backend restart killed
              // image gen mid-flight, an OpenAI call failed, etc.), poke the
              // /generate-personas endpoint — it'll detect the gap and retry
              // just the missing images in the background.
              const missing = research.personas.filter(p => !p.imageUrl).length;
              if (missing > 0) {
                console.log(`[FastResearch] ${missing} persona(s) missing image — triggering retry`);
                axios.post(`${getApiBaseURL()}/fast-research/${research._id}/generate-personas`).catch(err => {
                  console.error('[FastResearch] Retry kick failed:', err);
                });
              }
            } else if (research.status === 'failed' || research.status === 'generating_personas' || research.status === 'researching_context') {
              // Research failed or stuck — restart from website input
              console.log('[FastResearch] Research status:', research.status, '- restarting from WEBSITE_INPUT');
              restoredStep = STEPS.WEBSITE_INPUT;
            }

            console.log('[FastResearch] Restoring to step:', restoredStep);
            setStep(restoredStep);

            setRestoringState(false);
          }
        })
        .catch(err => {
          console.error('[FastResearch] Failed to restore state:', err);
          setRestoringState(false);
        });
    }
  }, []);

  // Helper to update step in database
  const updateStepInDB = async (stepNumber) => {
    if (!researchId) return;

    try {
      await axios.patch(
        `${getApiBaseURL()}/fast-research/${researchId}/step`,
        { currentStep: stepNumber }
      );
      console.log(`[FastResearch] Updated currentStep to ${stepNumber}`);
    } catch (err) {
      console.error('[FastResearch] Failed to update step:', err);
    }
  };

  const handleGoalSelect = (goal) => {
    if (goal === "something_else") {
      setStep(STEPS.CUSTOM_GOAL_INPUT);
    } else {
      setSelectedGoal(goal);
      console.log("Selected goal:", goal);
      setStep(STEPS.WEBSITE_INPUT);
    }
  };

  const normalizeUrl = (input) => {
    const trimmed = (input || '').trim();
    if (!trimmed) return trimmed;

    // Already has a protocol — leave alone
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }

    // Looks like a URL (has a dot, no spaces) — prepend https://
    // Otherwise it's a free-text topic ("cnn", "home fitness equipment") — pass through as-is
    const looksLikeUrl = trimmed.includes('.') && !/\s/.test(trimmed);
    return looksLikeUrl ? 'https://' + trimmed : trimmed;
  };

  const handleWebsiteSubmit = async () => {
    const normalized = normalizeUrl(websiteUrl);
    console.log("Scanning website:", normalized);

    setLoading(true);
    setError("");

    const apiStartTime = Date.now(); // Track when API call starts

    try {
      const response = await axios.post(
        `${getApiBaseURL()}/fast-research/scan-website`,
        {
          websiteUrl: normalized,
          language: language,
          goal: selectedGoal
        }
      );

      if (response.data.success) {
        console.log("Website scanned successfully:", response.data);
        console.log("[UltraFastResearch] screenshotUrl from API:", response.data.screenshotUrl);
        console.log("[UltraFastResearch] Setting scanResult with screenshot data");
        setScanResult(response.data);
        setResearchId(response.data.researchId); // Save research ID from scan

        // Update URL with research ID
        const newUrl = `/ultrafastresearch?id=${response.data.researchId}`;
        window.history.pushState({}, '', newUrl);
        console.log('[UltraFastResearch] Updated URL to:', newUrl);

        // Brief screenshot confirmation moment — long enough to show the user
        // we recognized their site, short enough not to feel like artificial wait.
        // The earlier overlay already explained what we were doing; this is just
        // the "captured" beat before advancing.
        const screenshotDisplayTime = 1500; // 1.5s confirmation
        console.log(`[UltraFastResearch] Showing screenshot confirmation for ${screenshotDisplayTime}ms`);

        // After the screenshot animation, advance to the refined-goals page.
        // Don't preselect — option 0 carries a "Recommended" badge so the user
        // gets the hint without being confused about whether they still need
        // to act.
        setTimeout(() => {
          console.log("[UltraFastResearch] Screenshot animation complete — showing refined goals");
          setLoading(false);
          setStep(STEPS.QUESTION_SELECTION);
        }, screenshotDisplayTime);
      } else {
        setError(response.data.error || "Failed to scan website");
        setLoading(false);
      }
    } catch (err) {
      console.error("Error scanning website:", err);
      setError(err.response?.data?.error || "Failed to scan website. Please try again.");
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(STEPS.GOAL_SELECTION);
    setError("");
  };

  // Fire-and-forget call to /generate-personas. Runs in parallel with the
  // checkmark animation + persona-screen transition so the wait happens behind
  // shimmer placeholders that gradually fill in.
  const kickOffPersonaGeneration = async (id) => {
    try {
      const response = await axios.post(`${getApiBaseURL()}/fast-research/${id}/generate-personas`);
      if (response.data.success && Array.isArray(response.data.personas)) {
        setPersonas(response.data.personas);
        setPersonaMetadata(response.data.metadata || null);
      } else {
        console.error('[FastResearch] generate-personas failed:', response.data);
        setError(response.data.error || "Failed to generate personas");
      }
    } catch (err) {
      console.error("[FastResearch] generate-personas error:", err);
      setError(err.response?.data?.error || "Failed to generate personas. Please try again.");
    }
  };

  const handleCreateResearch = async (indexToUse = null) => {
    const questionIndex = indexToUse !== null ? indexToUse : selectedQuestionIndex;
    if (questionIndex === null) return;

    setLoading(true);
    setError("");
    setDeepContextComplete(false);

    // Clear personas
    setPersonas(null);
    setPersonaMetadata(null);

    // Move to deep context research step
    setStep(STEPS.DEEP_CONTEXT_RESEARCH);

    try {
      const response = await axios.post(
        `${getApiBaseURL()}/fast-research/create`,
        {
          researchId: researchId,
          selectedQuestionIndex: questionIndex
        }
      );

      if (response.data.success) {
        console.log("Deep context complete:", response.data);
        const id = response.data.researchId;
        setResearchId(id);
        setImageCheckCount(0);
        // Five empty objects so the persona screen renders 5 shimmer placeholders
        // immediately when we transition to PERSONA_DISPLAY. Real persona text
        // arrives asynchronously via /generate-personas (fired below).
        setPersonas([{}, {}, {}, {}, {}]);
        setPersonaMetadata(null);
        setDeepContextComplete(true);
        // Fire persona generation in parallel — no await. The user advances to
        // the persona screen via the checkmark animation effect; persona text
        // populates a few seconds later as this resolves.
        kickOffPersonaGeneration(id);
      } else {
        setError(response.data.error || "Failed to create research");
        setStep(STEPS.WEBSITE_INPUT); // Go back on error
      }
    } catch (err) {
      console.error("Error creating research:", err);
      setError(err.response?.data?.error || "Failed to create research. Please try again.");
      setStep(STEPS.QUESTION_SELECTION); // Go back on error
    } finally {
      setLoading(false);
    }
  };


  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Countdown timer for deep context research
  useEffect(() => {
    if (step === STEPS.DEEP_CONTEXT_RESEARCH && !deepContextComplete) {
      setContextCountdown(15); // Reset to 15 seconds

      const interval = setInterval(() => {
        setContextCountdown(prev => {
          if (prev <= 0) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step, deepContextComplete]);

  // Rotate marketing messages on the deep-context overlay every 3.5s
  const deepContextMessages = (() => {
    const raw = t('ultra.rotatingMessages', { returnObjects: true });
    return Array.isArray(raw) && raw.length > 0 ? raw : [t('ultra.gatheringIntelligence')];
  })();
  useEffect(() => {
    if (step === STEPS.DEEP_CONTEXT_RESEARCH && completedSteps === 0) {
      // Random starting index so different sessions see different opening
      // messages (otherwise everyone sees the same first 3-4 in the same order).
      setDeepContextMessageIndex(Math.floor(Math.random() * deepContextMessages.length));
      const interval = setInterval(() => {
        setDeepContextMessageIndex(prev => (prev + 1) % deepContextMessages.length);
      }, 3000); // 3s rotation — all 5 messages cycle through during the 15s countdown
      return () => clearInterval(interval);
    }
  }, [step, completedSteps, deepContextMessages.length]);

  // Persona-generation messages — shown in the full-screen overlay while
  // /generate-personas is in flight (before text arrives).
  const personaMessages = (() => {
    const raw = t('ultra.personaRotatingMessages', { returnObjects: true });
    return Array.isArray(raw) && raw.length > 0 ? raw : [t('ultra.creatingProfiles')];
  })();
  // Portrait-generation messages — shown in the corner pill once persona
  // text is revealed and we're waiting on images.
  const portraitMessages = (() => {
    const raw = t('ultra.portraitRotatingMessages', { returnObjects: true });
    return Array.isArray(raw) && raw.length > 0 ? raw : personaMessages;
  })();
  // Persona text is "ready" once the /generate-personas response has populated
  // each entry with a name. Before that, the full-screen "generating personas"
  // overlay covers the screen.
  const personaTextReady = !!personas && personas.length > 0 && personas.every(p => p && p.name);
  // Stagger reveal completes when all 5 cards have animated in.
  const personaStaggerComplete = personaRevealStep >= (personas?.length || 5);
  // Full-screen overlay is shown while text is being generated (or stagger
  // hasn't started yet — guards against a flash where overlay disappears
  // before the first card appears).
  const personaTextOverlayShown = step === STEPS.PERSONA_DISPLAY && !personaTextReady;
  // Pill shows portrait progress only after stagger completes.
  const personaImagesPending = step === STEPS.PERSONA_DISPLAY && personaTextReady && personaStaggerComplete && !personas.every(p => p.imageUrl) && imageCheckCount < 30;

  // Rotate the overlay messages while text is being generated.
  useEffect(() => {
    if (personaTextOverlayShown) {
      setPersonaMessageIndex(Math.floor(Math.random() * personaMessages.length));
      const interval = setInterval(() => {
        setPersonaMessageIndex(prev => (prev + 1) % personaMessages.length);
      }, 6000); // 6s rotation — fits ~4 messages into the 25s overlay
      return () => clearInterval(interval);
    }
  }, [personaTextOverlayShown, personaMessages.length]);

  // Rotate the pill messages while images are loading.
  useEffect(() => {
    if (personaImagesPending) {
      setPortraitMessageIndex(Math.floor(Math.random() * portraitMessages.length));
      const interval = setInterval(() => {
        setPortraitMessageIndex(prev => (prev + 1) % portraitMessages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [personaImagesPending, portraitMessages.length]);

  // Staggered reveal: as soon as persona text arrives, animate cards in
  // one-by-one over ~2s (400ms between each). The personas themselves were
  // generated together — this is purely a visual "introduction" so each
  // persona gets a moment of recognition before the next one lands.
  useEffect(() => {
    if (personaTextReady && personaRevealStep === 0) {
      const total = personas.length;
      const timers = [];
      for (let i = 1; i <= total; i++) {
        timers.push(setTimeout(() => setPersonaRevealStep(i), i * 400));
      }
      return () => timers.forEach(clearTimeout);
    }
    // Reset reveal when leaving the persona screen
    if (step !== STEPS.PERSONA_DISPLAY && personaRevealStep !== 0) {
      setPersonaRevealStep(0);
    }
  }, [personaTextReady, step]);

  // Countdown timer for persona generation
  useEffect(() => {
    // Start countdown only once when entering persona display step
    if (step === STEPS.PERSONA_DISPLAY && !personaCountdownStartedRef.current) {
      personaCountdownStartedRef.current = true;
      setPersonaCountdown(25); // Reset to 25 seconds

      const interval = setInterval(() => {
        setPersonaCountdown(prev => {
          if (prev <= 0) {
            clearInterval(interval);
            return 0;
          }
          // Continue counting down regardless of persona loading status
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }

    // Reset the ref when leaving persona display step
    if (step !== STEPS.PERSONA_DISPLAY) {
      personaCountdownStartedRef.current = false;
      personasArrivedPingedRef.current = false;
    }
  }, [step]); // Only depend on step, not personas

  // Ping once when personas arrive on the persona display step
  useEffect(() => {
    if (personasArrivedPingedRef.current) return;
    if (step === STEPS.PERSONA_DISPLAY && personas && personas.length > 0) {
      personasArrivedPingedRef.current = true;
      notifyUser({ frequency: 760, vibratePattern: 30, gain: 0.07, durationMs: 200 });
    }
  }, [step, personas?.length]);

  // Animate checkmarks appearing one by one (2 seconds apart) AFTER API completes
  useEffect(() => {
    // Reset when entering the step
    if (step === STEPS.DEEP_CONTEXT_RESEARCH && !deepContextComplete) {
      setCompletedSteps(0);
    }

    // When research API completes successfully, start the checkmark animation.
    // Snappy 500ms cadence — the user has already waited ~30s for industry
    // research, so don't make them sit through another 7s of theatrical
    // checkmarks before they get to the personas.
    if (step === STEPS.DEEP_CONTEXT_RESEARCH && deepContextComplete) {
      const timers = [
        setTimeout(() => setCompletedSteps(1), 0),    // Immediate
        setTimeout(() => setCompletedSteps(2), 500),
        setTimeout(() => setCompletedSteps(3), 1000),
        setTimeout(() => setCompletedSteps(4), 1500),
        setTimeout(() => {
          // Auto-advance to personas shortly after last checkmark
          setStep(STEPS.PERSONA_DISPLAY);
        }, 2100) // +600ms after last checkmark for smooth transition
      ];

      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [step, deepContextComplete]);

  // Poll for persona images (they're generated asynchronously).
  // Cap at ~90s — image generation occasionally exceeds 30s for an outlier
  // persona, so the prior 30s window cut off polling before the slowest
  // image had a chance to land. After the cap, the UI will fall back to
  // showing initials placeholders for any persona still missing an image.
  const IMAGE_POLL_LIMIT = 30; // 30 polls × 3s = 90s
  useEffect(() => {
    if (step === STEPS.PERSONA_DISPLAY && researchId && personas && personas.length > 0 && imageCheckCount < IMAGE_POLL_LIMIT) {
      // Check if we need to poll for images
      const hasAllImages = personas.every(p => p.imageUrl);

      if (!hasAllImages) {
        const timer = setTimeout(async () => {
          try {
            const response = await axios.get(`${getApiBaseURL()}/fast-research/${researchId}`);
            if (response.data.success && response.data.research.personas && response.data.research.personas.length > 0) {
              // Only update if we got personas back (don't overwrite with empty array)
              setPersonas(response.data.research.personas);
              setImageCheckCount(prev => prev + 1);
            }
          } catch (err) {
            console.error("Error fetching updated personas:", err);
          }
        }, 3000); // Check every 3 seconds

        return () => clearTimeout(timer);
      }
    }
  }, [step, researchId, imageCheckCount, personas]);

  // After polling exhausts, accept whatever images we have and let the user
  // proceed — the missing slots already show initials placeholders.
  const imagePollExhausted = imageCheckCount >= IMAGE_POLL_LIMIT;

  const handleContinueFromPersonas = () => {
    setStep(STEPS.EMAIL_COLLECTION);
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${getApiBaseURL()}/fast-research/${researchId}/start-full-research`,
        {
          email: email.trim()
        }
      );

      if (response.data.success) {
        console.log("Full research started with email:", email);
        // Redirect to progress page immediately
        navigate(`/ultrafastresearch/${researchId}/progress`);
      } else {
        setError(response.data.error || "Failed to start research");
      }
    } catch (err) {
      console.error("Error starting research:", err);
      setError(err.response?.data?.error || "Failed to start research. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while restoring
  if (restoringState) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem"
      }}>
        <div style={{
          textAlign: "center",
          color: "#fff"
        }}>
          <h2 style={{
            fontSize: "clamp(1.5rem, 5vw, 2rem)",
            fontWeight: "900",
            marginBottom: "1rem",
            color: "#a78bfa"
          }}>
            {t('ultra.restoring')}
          </h2>
          <p style={{
            fontSize: "1rem",
            color: "#888"
          }}>
            {t('ultra.pleaseWait')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeOut {
            0% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }
          @keyframes skeletonPulse {
            0%, 100% { opacity: 0.55; }
            50% { opacity: 0.95; }
          }
          @keyframes slideUpFadeIn {
            0% { transform: translateY(12px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes spinReverse {
            0% { transform: rotate(360deg); }
            100% { transform: rotate(0deg); }
          }
          @keyframes particle0 {
            0% {
              transform: translate(-60px, -60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }
          @keyframes particle1 {
            0% {
              transform: translate(60px, -60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }
          @keyframes particle2 {
            0% {
              transform: translate(60px, 60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }
          @keyframes particle3 {
            0% {
              transform: translate(-60px, 60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }
          .mobile-truncate {
            display: inline;
          }
          .desktop-full {
            display: none;
          }
          .mobile-only {
            display: grid !important;
          }
          .desktop-only {
            display: none !important;
          }
          .mobile-demographics {
            display: none !important;
          }
          /* Show demographics on taller mobile screens (>700px height) */
          @media (max-width: 767px) and (min-height: 700px) {
            .mobile-demographics {
              display: block !important;
            }
          }
          @media (min-width: 768px) {
            .mobile-truncate {
              display: none;
            }
            .desktop-full {
              display: inline;
            }
            .mobile-only {
              display: none !important;
            }
            .desktop-only {
              display: grid !important;
            }
          }
        `}
      </style>
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1.25rem",
        paddingTop: "2.5rem",
        backgroundColor: "#000",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}>
      <div style={{
        width: "100%",
        maxWidth: "700px",
        display: "flex",
        flexDirection: "column"
      }}>
          {/* Step 1: Goal Selection */}
          {step === STEPS.GOAL_SELECTION && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <GoalSelection
                selectedGoal={selectedGoal}
                onGoalSelect={handleGoalSelect}
              />
            </div>
          )}

          {/* Step 1.5: Custom Goal Input */}
          {step === STEPS.CUSTOM_GOAL_INPUT && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <CustomGoalInput
                onAISuggestions={() => {
                  setSelectedGoal("suggest_something");
                  setStep(STEPS.WEBSITE_INPUT);
                }}
                onCustomGoal={(customText) => {
                  setSelectedGoal("custom:" + customText);
                  setStep(STEPS.WEBSITE_INPUT);
                }}
                onBack={() => setStep(STEPS.GOAL_SELECTION)}
              />
            </div>
          )}

          {/* Step 2: Website Input */}
          {step === STEPS.WEBSITE_INPUT && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <WebsiteInput
                websiteUrl={websiteUrl}
                onWebsiteChange={setWebsiteUrl}
                onBack={handleBack}
                onContinue={handleWebsiteSubmit}
                loading={loading}
                error={error}
                screenshotUrl={scanResult?.screenshotUrl}
              />
            </div>
          )}

          {/* Step 3: Question Selection */}
          {step === STEPS.QUESTION_SELECTION && scanResult && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <QuestionSelection
                questions={scanResult.suggestedQuestions}
                selectedIndex={selectedQuestionIndex}
                onSelect={setSelectedQuestionIndex}
                onContinue={handleCreateResearch}
                loading={loading}
                screenshotUrl={scanResult.screenshotUrl}
                metadata={scanResult.metadata}
              />
            </div>
          )}

          {/* Step 4: Deep Context Research Loading */}
          {step === STEPS.DEEP_CONTEXT_RESEARCH && (
            <>
              <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <h1 style={{
                fontSize: "clamp(2rem, 8vw, 2.75rem)",
                marginBottom: "1rem",
                fontWeight: "900",
                color: "#fff",
                letterSpacing: "-0.04em",
                lineHeight: "1",
                textAlign: "center"
              }}>
                {deepContextComplete ? t('ultra.contextGathered') : t('ultra.researchingIndustry')}
              </h1>

              <p style={{
                fontSize: "clamp(0.9375rem, 3vw, 1rem)",
                color: "#888",
                marginBottom: "2rem",
                lineHeight: "1.5",
                textAlign: "center"
              }}>
                {deepContextComplete
                  ? t('ultra.marketIntelligenceGathered')
                  : t('ultra.analyzingMarket')
                }
              </p>

              <div style={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "10px",
                padding: "1.5rem",
                marginBottom: "2rem",
                textAlign: "left"
              }}>
                {[
                  { id: 1, title: t('ultra.industryDynamics') },
                  { id: 2, title: t('ultra.competitiveLandscape') },
                  { id: 3, title: t('ultra.marketTrends') },
                  { id: 4, title: t('ultra.customerSegments') }
                ].map(item => (
                  <div key={item.id} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: item.id < 4 ? "1rem" : "0",
                    padding: "0.75rem 1rem",
                    borderRadius: "8px",
                    backgroundColor: completedSteps >= item.id ? "rgba(99, 102, 241, 0.15)" : "rgba(255, 255, 255, 0.03)",
                    border: completedSteps >= item.id ? "1px solid rgba(99, 102, 241, 0.3)" : "1px solid rgba(255, 255, 255, 0.08)",
                    transition: "all 0.3s ease"
                  }}>
                    <div style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: completedSteps >= item.id ? "#6366f1" : "rgba(255, 255, 255, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      color: "white",
                      fontWeight: "700",
                      flexShrink: 0,
                      transition: "all 0.3s ease"
                    }}>
                      {completedSteps >= item.id ? "✓" : ""}
                    </div>
                    <div style={{
                      fontSize: "clamp(0.8125rem, 3vw, 0.875rem)",
                      fontWeight: "600",
                      color: completedSteps >= item.id ? "#a78bfa" : "#666",
                      flex: 1,
                      transition: "color 0.3s ease"
                    }}>
                      {item.title}
                    </div>
                  </div>
                ))}
              </div>

              </div>

              {/* Overlay with spinning animation while researching */}
              {completedSteps === 0 && (
                <div style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  backdropFilter: "blur(2px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                  animation: "fadeIn 0.3s ease"
                }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem"
                  }}>
                    {/* Animated particle container */}
                    <div style={{
                      position: "relative",
                      width: "120px",
                      height: "120px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      {/* Countdown number in center */}
                      <div style={{
                        position: "absolute",
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#a78bfa",
                        textAlign: "center",
                        lineHeight: "1",
                        zIndex: 10
                      }}>
                        {contextCountdown}
                        <div style={{
                          fontSize: "0.5rem",
                          fontWeight: "600",
                          color: "rgba(167, 139, 250, 0.6)",
                          marginTop: "2px",
                          letterSpacing: "0.5px"
                        }}>
                          {t('ultra.sec')}
                        </div>
                      </div>

                      {/* Outer rotating ring */}
                      <div style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        border: "2px solid rgba(99, 102, 241, 0.3)",
                        borderRadius: "50%",
                        animation: "spin 3s linear infinite"
                      }} />

                      {/* Middle rotating ring (opposite direction) */}
                      <div style={{
                        position: "absolute",
                        width: "80px",
                        height: "80px",
                        border: "2px solid rgba(167, 139, 250, 0.4)",
                        borderTopColor: "#a78bfa",
                        borderRadius: "50%",
                        animation: "spinReverse 2s linear infinite"
                      }} />

                      {/* Particles flowing in */}
                      {[...Array(8)].map((_, i) => (
                        <div key={i} style={{
                          position: "absolute",
                          width: "4px",
                          height: "4px",
                          backgroundColor: "#6366f1",
                          borderRadius: "50%",
                          boxShadow: "0 0 12px #6366f1",
                          animation: `particle${i % 4} 2s ease-in-out infinite`,
                          animationDelay: `${i * 0.25}s`,
                          opacity: 0
                        }} />
                      ))}
                    </div>

                    {/* Loading text */}
                    <div style={{ textAlign: "center", minHeight: "2.5rem" }}>
                      <p
                        key={deepContextMessageIndex}
                        style={{
                          fontSize: "clamp(0.9375rem, 3vw, 1rem)",
                          fontWeight: "700",
                          color: "#fff",
                          marginBottom: 0,
                          animation: "fadeIn 0.4s ease"
                        }}
                      >
                        {deepContextMessages[deepContextMessageIndex]}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 5: Personas Display */}
          {step === STEPS.PERSONA_DISPLAY && (
            <>
              <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
                <h1 style={{
                    fontSize: "clamp(2rem, 8vw, 2.75rem)",
                    marginBottom: "1rem",
                    fontWeight: "900",
                    color: "#fff",
                    letterSpacing: "-0.04em",
                    lineHeight: "1",
                    textAlign: "center"
                  }}>
                    {personaTextReady && personaStaggerComplete && (personas.every(p => p.imageUrl) || imageCheckCount >= 30) ? t('ultra.personasGenerated') : t('ultra.generatingPersonas')}
                </h1>

                {/* Mobile view - 2 column grid */}
                <div className="mobile-only" style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.75rem",
                    marginBottom: "1.25rem",
                    marginTop: "1.25rem"
                  }}>
                    {personas && personas.map((persona, index) => {
                      // Shimmer placeholder while persona text is still loading
                      // Card stays as shimmer if (a) no text yet, OR (b) text
                      // is here but staggered reveal hasn't reached this index.
                      if (!persona || !persona.name || index >= personaRevealStep) {
                        return (
                          <div key={index} style={{
                            padding: "0.75rem",
                            borderRadius: "10px",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center"
                          }}>
                            <div style={{
                              width: "75px", height: "75px", borderRadius: "50%",
                              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(167, 139, 250, 0.1))",
                              border: "2px solid rgba(167, 139, 250, 0.25)",
                              marginBottom: "0.6rem",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }} />
                            <div style={{
                              width: "70%", height: "12px", borderRadius: "4px",
                              backgroundColor: "rgba(167, 139, 250, 0.15)",
                              marginBottom: "0.4rem",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }} />
                            <div style={{
                              width: "45%", height: "10px", borderRadius: "4px",
                              backgroundColor: "rgba(167, 139, 250, 0.1)",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }} />
                          </div>
                        );
                      }

                      // Debug log the first persona to see what data we have
                      if (index === 0) {
                        console.log('[FastResearch Persona Debug]', {
                          name: persona.name,
                          hasAttributes: !!persona.attributes,
                          attributes: persona.attributes,
                          detailedText: persona.detailedText?.substring(0, 200)
                        });
                      }

                      // Extract demographics - prioritize attributes, then parse detailedText
                      let age = t('demographics.locations.unknown');
                      let gender = t('demographics.genders.unknown');
                      let location = t('demographics.locations.unknown');

                      // First try to get from attributes (structured data)
                      if (persona.attributes && persona.attributes.demographics) {
                        if (persona.attributes.demographics.age && persona.attributes.demographics.age.value !== undefined) {
                          age = persona.attributes.demographics.age.value;
                        }
                        if (persona.attributes.demographics.gender && persona.attributes.demographics.gender.value !== undefined) {
                          const genderKeyMap = { 1: "female", 2: "male", 3: "nonBinary", 4: "other" };
                          const genderKey = genderKeyMap[persona.attributes.demographics.gender.value] || "unknown";
                          gender = t(`demographics.genders.${genderKey}`);
                        }
                        if (persona.attributes.demographics.location && persona.attributes.demographics.location.value) {
                          const locValue = persona.attributes.demographics.location.value.toLowerCase();
                          const locKey = ['urban', 'suburban', 'rural'].includes(locValue) ? locValue : null;
                          location = locKey ? t(`demographics.locations.${locKey}`) : persona.attributes.demographics.location.value;
                        }
                      } else if (persona.detailedText) {
                        // Fallback: Extract from detailedText if attributes not available
                        const ageMatch = persona.detailedText.match(/Age:\s*(\d+)/i);
                        if (ageMatch) age = ageMatch[1];

                        const genderMatch = persona.detailedText.match(/Gender:\s*(\w+)/i);
                        if (genderMatch) {
                          const gKey = genderMatch[1].toLowerCase();
                          const gKeyMap = { 'female': 'female', 'male': 'male', 'non-binary': 'nonBinary' };
                          gender = t(`demographics.genders.${gKeyMap[gKey] || 'other'}`);
                        }

                        const locationMatch = persona.detailedText.match(/Location:\s*([^,\n]+)/i);
                        if (locationMatch) {
                          const lKey = locationMatch[1].trim().toLowerCase();
                          const lKeyMap = { 'urban': 'urban', 'suburban': 'suburban', 'rural': 'rural' };
                          location = lKeyMap[lKey] ? t(`demographics.locations.${lKeyMap[lKey]}`) : locationMatch[1].trim();
                        }
                      }

                      return (
                        <div
                          key={index}
                          style={{
                            padding: "0.75rem",
                            borderRadius: "10px",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                            opacity: persona.imageUrl ? 1 : 0.85,
                            transition: "opacity 0.4s ease",
                            animation: "fadeIn 0.5s ease"
                          }}
                        >
                          {persona.imageUrl ? (
                            <img
                              src={persona.imageUrl}
                              alt={persona.name}
                              style={{
                                width: "75px",
                                height: "75px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid rgba(99, 102, 241, 0.3)",
                                marginBottom: "0.4rem",
                                animation: "fadeIn 0.5s ease"
                              }}
                            />
                          ) : (
                            <div style={{
                              width: "75px",
                              height: "75px",
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(167, 139, 250, 0.15))",
                              border: "2px solid rgba(167, 139, 250, 0.35)",
                              marginBottom: "0.4rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "rgba(255, 255, 255, 0.85)",
                              fontWeight: 800,
                              fontSize: "1.4rem",
                              letterSpacing: "0.02em",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }}>
                              {getPersonaInitials(persona.name)}
                            </div>
                          )}
                          <div style={{
                            fontSize: "clamp(0.875rem, 3vw, 0.9375rem)",
                            fontWeight: "700",
                            color: "#fff",
                            lineHeight: "1.2",
                            marginBottom: "0.25rem"
                          }}>
                            {persona.name}
                          </div>
                          {/* Show demographics only on taller mobile screens */}
                          <div className="mobile-demographics" style={{
                            fontSize: "clamp(0.7rem, 2.5vw, 0.75rem)",
                            color: "#999",
                            lineHeight: "1.2",
                            display: "none"
                          }}>
                            {age}, {gender}
                            <br />
                            {location}
                          </div>
                        </div>
                      );
                    })}
                    {/* Progress card slotted into the empty 6th cell of the
                        grid (5 personas + 1 progress slot). Disappears once
                        all images are loaded or polling exhausts. */}
                    {personaImagesPending && (() => {
                      const readyCount = personas.filter(p => p.imageUrl).length;
                      return (
                        <div style={{
                          padding: "0.75rem",
                          borderRadius: "10px",
                          backgroundColor: "rgba(99, 102, 241, 0.08)",
                          border: "1px dashed rgba(167, 139, 250, 0.35)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          animation: "fadeIn 0.4s ease"
                        }}>
                          <div style={{
                            position: "relative",
                            width: "75px",
                            height: "75px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "0.4rem"
                          }}>
                            <div style={{
                              position: "absolute",
                              inset: 0,
                              border: "2px solid rgba(99, 102, 241, 0.25)",
                              borderTopColor: "#a78bfa",
                              borderRadius: "50%",
                              animation: "spin 1.4s linear infinite"
                            }} />
                            <div style={{
                              fontSize: "0.95rem",
                              fontWeight: 800,
                              color: "#a78bfa",
                              lineHeight: 1
                            }}>
                              {readyCount}/{personas.length}
                            </div>
                          </div>
                          <div
                            key={portraitMessageIndex}
                            style={{
                              fontSize: "clamp(0.7rem, 2.5vw, 0.75rem)",
                              fontWeight: 600,
                              color: "#bbb",
                              lineHeight: 1.2,
                              animation: "fadeIn 0.4s ease",
                              minHeight: "1.5rem"
                            }}
                          >
                            {portraitMessages[portraitMessageIndex]}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Desktop view - 3 column grid */}
                  <div className="desktop-only" style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "1rem",
                    marginBottom: "2rem",
                    marginTop: "1.5rem"
                  }}>
                    {personas && personas.map((persona, index) => {
                      // Shimmer placeholder while persona text is still loading
                      // Card stays as shimmer if (a) no text yet, OR (b) text
                      // is here but staggered reveal hasn't reached this index.
                      if (!persona || !persona.name || index >= personaRevealStep) {
                        return (
                          <div key={index} style={{
                            padding: "1.25rem",
                            borderRadius: "10px",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center"
                          }}>
                            <div style={{
                              width: "80px", height: "80px", borderRadius: "50%",
                              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(167, 139, 250, 0.1))",
                              border: "2px solid rgba(167, 139, 250, 0.25)",
                              marginBottom: "0.85rem",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }} />
                            <div style={{
                              width: "75%", height: "14px", borderRadius: "4px",
                              backgroundColor: "rgba(167, 139, 250, 0.18)",
                              marginBottom: "0.5rem",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }} />
                            <div style={{
                              width: "55%", height: "11px", borderRadius: "4px",
                              backgroundColor: "rgba(167, 139, 250, 0.12)",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }} />
                          </div>
                        );
                      }

                      // Debug log the first persona to see what data we have
                      if (index === 0) {
                        console.log('[FastResearch Persona Debug]', {
                          name: persona.name,
                          hasAttributes: !!persona.attributes,
                          attributes: persona.attributes,
                          detailedText: persona.detailedText?.substring(0, 200)
                        });
                      }

                      // Extract demographics - prioritize attributes, then parse detailedText
                      let age = t('demographics.locations.unknown');
                      let gender = t('demographics.genders.unknown');
                      let location = t('demographics.locations.unknown');

                      // First try to get from attributes (structured data)
                      if (persona.attributes && persona.attributes.demographics) {
                        if (persona.attributes.demographics.age && persona.attributes.demographics.age.value !== undefined) {
                          age = persona.attributes.demographics.age.value;
                        }
                        if (persona.attributes.demographics.gender && persona.attributes.demographics.gender.value !== undefined) {
                          const genderKeyMap = { 1: "female", 2: "male", 3: "nonBinary", 4: "other" };
                          const genderKey = genderKeyMap[persona.attributes.demographics.gender.value] || "unknown";
                          gender = t(`demographics.genders.${genderKey}`);
                        }
                        if (persona.attributes.demographics.location && persona.attributes.demographics.location.value) {
                          const locValue = persona.attributes.demographics.location.value.toLowerCase();
                          const locKey = ['urban', 'suburban', 'rural'].includes(locValue) ? locValue : null;
                          location = locKey ? t(`demographics.locations.${locKey}`) : persona.attributes.demographics.location.value;
                        }
                      } else if (persona.detailedText) {
                        // Fallback: Extract from detailedText if attributes not available
                        const ageMatch = persona.detailedText.match(/Age:\s*(\d+)/i);
                        if (ageMatch) age = ageMatch[1];

                        const genderMatch = persona.detailedText.match(/Gender:\s*(\w+)/i);
                        if (genderMatch) {
                          const gKey = genderMatch[1].toLowerCase();
                          const gKeyMap = { 'female': 'female', 'male': 'male', 'non-binary': 'nonBinary' };
                          gender = t(`demographics.genders.${gKeyMap[gKey] || 'other'}`);
                        }

                        const locationMatch = persona.detailedText.match(/Location:\s*([^,\n]+)/i);
                        if (locationMatch) {
                          const lKey = locationMatch[1].trim().toLowerCase();
                          const lKeyMap = { 'urban': 'urban', 'suburban': 'suburban', 'rural': 'rural' };
                          location = lKeyMap[lKey] ? t(`demographics.locations.${lKeyMap[lKey]}`) : locationMatch[1].trim();
                        }
                      }

                      return (
                        <div
                          key={index}
                          style={{
                            padding: "1.25rem",
                            borderRadius: "10px",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                            opacity: persona.imageUrl ? 1 : 0.85,
                            transition: "opacity 0.4s ease",
                            animation: "fadeIn 0.5s ease"
                          }}
                        >
                          {persona.imageUrl ? (
                            <img
                              src={persona.imageUrl}
                              alt={persona.name}
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid rgba(99, 102, 241, 0.3)",
                                marginBottom: "0.75rem",
                                animation: "fadeIn 0.5s ease"
                              }}
                            />
                          ) : (
                            <div style={{
                              width: "80px",
                              height: "80px",
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(167, 139, 250, 0.15))",
                              border: "2px solid rgba(167, 139, 250, 0.35)",
                              marginBottom: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "rgba(255, 255, 255, 0.85)",
                              fontWeight: 800,
                              fontSize: "1.5rem",
                              letterSpacing: "0.02em",
                              animation: "skeletonPulse 1.6s ease-in-out infinite"
                            }}>
                              {getPersonaInitials(persona.name)}
                            </div>
                          )}
                          <div style={{ width: "100%" }}>
                            <div style={{
                              fontSize: "1rem",
                              fontWeight: "700",
                              color: "#fff",
                              marginBottom: "0.25rem",
                              lineHeight: "1.3"
                            }}>
                              {persona.name}
                            </div>
                            <div style={{
                              fontSize: "0.8125rem",
                              color: "#a78bfa",
                              lineHeight: "1.3"
                            }}>
                              {age}, {gender} • {location}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Progress card slotted into the empty 6th cell of the
                        3-column grid (5 personas + 1 progress slot).
                        Disappears once all images are loaded or polling
                        exhausts. */}
                    {personaImagesPending && (() => {
                      const readyCount = personas.filter(p => p.imageUrl).length;
                      return (
                        <div style={{
                          padding: "1.25rem",
                          borderRadius: "10px",
                          backgroundColor: "rgba(99, 102, 241, 0.08)",
                          border: "1px dashed rgba(167, 139, 250, 0.35)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          animation: "fadeIn 0.4s ease"
                        }}>
                          <div style={{
                            position: "relative",
                            width: "80px",
                            height: "80px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "0.75rem"
                          }}>
                            <div style={{
                              position: "absolute",
                              inset: 0,
                              border: "2px solid rgba(99, 102, 241, 0.25)",
                              borderTopColor: "#a78bfa",
                              borderRadius: "50%",
                              animation: "spin 1.4s linear infinite"
                            }} />
                            <div style={{
                              fontSize: "1rem",
                              fontWeight: 800,
                              color: "#a78bfa",
                              lineHeight: 1
                            }}>
                              {readyCount}/{personas.length}
                            </div>
                          </div>
                          <div
                            key={portraitMessageIndex}
                            style={{
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              color: "#bbb",
                              lineHeight: 1.3,
                              animation: "fadeIn 0.4s ease",
                              minHeight: "1.6rem"
                            }}
                          >
                            {portraitMessages[portraitMessageIndex]}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Inline email capture — visible immediately so the user can
                      type while persona text/images are still streaming in.
                      Submit is enabled once email is valid AND persona text is
                      loaded (don't gate on images — those are decorative now). */}
                  <div style={{ marginTop: "1.5rem", textAlign: "left" }}>
                    <p style={{
                      fontSize: "clamp(0.875rem, 2.8vw, 0.9375rem)",
                      color: "#bbb",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      textAlign: "center"
                    }}>
                      {t('ultra.whereToSend')}
                    </p>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck="false"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing && isValidEmail(email) && personaTextReady && !loading) {
                          handleEmailSubmit();
                        }
                      }}
                      placeholder={t('ultra.emailPlaceholder')}
                      disabled={loading}
                      style={{
                        width: "100%",
                        padding: "1rem 1.125rem",
                        fontSize: "clamp(0.9375rem, 3.5vw, 1rem)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        borderRadius: "10px",
                        outline: "none",
                        marginBottom: "0.75rem",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        color: "#fff",
                        textAlign: "center",
                        fontWeight: "600"
                      }}
                    />
                    <button
                      onClick={handleEmailSubmit}
                      disabled={!isValidEmail(email) || !personaTextReady || loading}
                      style={{
                        width: "100%",
                        padding: "clamp(1rem, 3vh, 1.25rem)",
                        fontSize: "clamp(0.9375rem, 3.5vw, 1rem)",
                        fontWeight: "800",
                        border: "none",
                        borderRadius: "10px",
                        backgroundColor: (isValidEmail(email) && personaTextReady && !loading) ? "#fff" : "rgba(255, 255, 255, 0.1)",
                        color: (isValidEmail(email) && personaTextReady && !loading) ? "#000" : "#666",
                        cursor: (isValidEmail(email) && personaTextReady && !loading) ? "pointer" : "not-allowed",
                        transition: "all 0.3s ease",
                        letterSpacing: "0.02em"
                      }}
                      onMouseEnter={(e) => {
                        if (isValidEmail(email) && personaTextReady && !loading) {
                          e.currentTarget.style.backgroundColor = "#a78bfa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isValidEmail(email) && personaTextReady && !loading) {
                          e.currentTarget.style.backgroundColor = "#fff";
                        }
                      }}
                    >
                      {loading ? t('ultra.startingResearch') : t('ultra.getFreeSample')}
                    </button>
                  </div>
              </div>

              {/* Full-screen overlay while persona text is being generated.
                  Hides the shimmer cards behind it so the user gets a clear
                  "generating personas" beat before the staggered reveal. */}
              {personaTextOverlayShown && (
                <div style={{
                  position: "fixed",
                  inset: 0,
                  backgroundColor: "rgba(10, 8, 22, 0.78)",
                  backdropFilter: "blur(4px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                  animation: "fadeIn 0.3s ease"
                }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem"
                  }}>
                    <div style={{
                      position: "relative",
                      width: "120px",
                      height: "120px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      {/* Countdown number in the middle */}
                      <div style={{
                        position: "absolute",
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#a78bfa",
                        textAlign: "center",
                        lineHeight: 1,
                        zIndex: 10
                      }}>
                        {personaCountdown}
                        <div style={{
                          fontSize: "0.5rem",
                          fontWeight: "600",
                          color: "rgba(167, 139, 250, 0.6)",
                          marginTop: "2px",
                          letterSpacing: "0.5px"
                        }}>
                          {t('ultra.sec')}
                        </div>
                      </div>
                      <div style={{
                        position: "absolute",
                        inset: 0,
                        border: "2px solid rgba(99, 102, 241, 0.3)",
                        borderTopColor: "#a78bfa",
                        borderRadius: "50%",
                        animation: "spin 1.4s linear infinite"
                      }} />
                      <div style={{
                        position: "absolute",
                        width: "80px",
                        height: "80px",
                        border: "2px solid rgba(167, 139, 250, 0.4)",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spinReverse 2.4s linear infinite"
                      }} />
                    </div>
                    <div style={{ textAlign: "center", minHeight: "2.5rem", maxWidth: "20rem", padding: "0 1rem" }}>
                      <p style={{
                        fontSize: "clamp(1rem, 3.2vw, 1.05rem)",
                        fontWeight: "700",
                        color: "#fff",
                        marginBottom: "0.35rem"
                      }}>
                        {t('ultra.generatingPersonas')}
                      </p>
                      <p
                        key={personaMessageIndex}
                        style={{
                          fontSize: "clamp(0.8125rem, 2.5vw, 0.875rem)",
                          color: "#bbb",
                          margin: 0,
                          animation: "fadeIn 0.4s ease"
                        }}
                      >
                        {personaMessages[personaMessageIndex]}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 7: Email Collection - Full Screen Mobile Friendly */}
          {step === STEPS.EMAIL_COLLECTION && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <h1 style={{
                fontSize: "clamp(2rem, 8vw, 3rem)",
                marginBottom: "0.5rem",
                fontWeight: "900",
                color: "#fff",
                letterSpacing: "-0.04em",
                lineHeight: "0.9"
              }}>
                {t('ultra.marketResearch')}<br/>
                <span style={{ color: "#a78bfa" }}>{t('ultra.doneFast')}</span>
              </h1>

              <p style={{
                fontSize: "clamp(0.875rem, 3vw, 1rem)",
                color: "#888",
                marginBottom: "1.5rem"
              }}>
                {t('ultra.withSyntheticData')}
              </p>

              {/* Value props matching homepage */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0.75rem",
                marginBottom: "2rem",
                padding: "0 1rem"
              }}>
                <div style={{
                  padding: "1rem",
                  backgroundColor: "rgba(167, 139, 250, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(167, 139, 250, 0.2)"
                }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.25rem", fontWeight: "700" }}>5</div>
                  <div style={{ fontSize: "0.75rem", color: "#a78bfa" }}>{t('ultra.personas')}</div>
                </div>
                <div style={{
                  padding: "1rem",
                  backgroundColor: "rgba(167, 139, 250, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(167, 139, 250, 0.2)"
                }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.25rem", fontWeight: "700" }}>5</div>
                  <div style={{ fontSize: "0.75rem", color: "#a78bfa" }}>{t('ultra.interviews')}</div>
                </div>
                <div style={{
                  padding: "1rem",
                  backgroundColor: "rgba(167, 139, 250, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(167, 139, 250, 0.2)"
                }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.25rem", fontWeight: "700" }}>50</div>
                  <div style={{ fontSize: "0.75rem", color: "#a78bfa" }}>{t('ultra.responses')}</div>
                </div>
                <div style={{
                  padding: "1rem",
                  backgroundColor: "rgba(167, 139, 250, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(167, 139, 250, 0.2)"
                }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.25rem", fontWeight: "700" }}>~50</div>
                  <div style={{ fontSize: "0.75rem", color: "#a78bfa" }}>{t('ultra.pageReport')}</div>
                </div>
              </div>

              {/* Progress indicator */}
              <div style={{
                width: "100%",
                height: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderRadius: "4px",
                marginBottom: "0.75rem",
                overflow: "hidden"
              }}>
                <div style={{
                  width: "20%",
                  height: "100%",
                  backgroundColor: "#a78bfa",
                  borderRadius: "4px",
                  animation: "pulse 2s ease-in-out infinite"
                }}/>
              </div>
              <p style={{
                fontSize: "0.875rem",
                color: "#999",
                marginBottom: "2rem"
              }}>
                {t('ultra.takesMinutes')}
              </p>

              {/* Email form */}
              <div style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                borderRadius: "12px",
                padding: "1.5rem",
                border: "1px solid rgba(255, 255, 255, 0.1)"
              }}>
                <h2 style={{
                  fontSize: "1.125rem",
                  fontWeight: "700",
                  color: "#fff",
                  marginBottom: "1rem"
                }}>
                  {t('ultra.whereToSend')}
                </h2>

                <input
                  type="email"
                  placeholder={t('ultra.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "1rem",
                    fontSize: "1.0625rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "8px",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    color: "#fff",
                    marginBottom: "1rem",
                    outline: "none"
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#a78bfa";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                  }}
                  autoFocus
                />

                <button
                  onClick={handleEmailSubmit}
                  disabled={!isValidEmail(email) || loading}
                  style={{
                    width: "100%",
                    padding: "clamp(1rem, 3vh, 1.25rem)",
                    fontSize: "clamp(0.9375rem, 3.5vw, 1rem)",
                    fontWeight: "800",
                    border: "none",
                    borderRadius: "10px",
                    backgroundColor: (isValidEmail(email) && !loading) ? "#fff" : "rgba(255, 255, 255, 0.1)",
                    color: (isValidEmail(email) && !loading) ? "#000" : "#555",
                    cursor: (isValidEmail(email) && !loading) ? "pointer" : "not-allowed",
                    transition: "all 0.3s ease",
                    letterSpacing: "0.02em",
                    marginBottom: "0.75rem"
                  }}
                  onMouseEnter={(e) => {
                    if (isValidEmail(email) && !loading) {
                      e.currentTarget.style.backgroundColor = "#a78bfa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = (isValidEmail(email) && !loading) ? "#fff" : "rgba(255, 255, 255, 0.1)";
                  }}
                >
                  {loading ? t('ultra.startingResearch') : t('ultra.getFreeSample')}
                </button>

                <p style={{
                  fontSize: "0.75rem",
                  color: "#666"
                }}>
                  {t('ultra.safeToClose')}
                </p>
              </div>

              {error && (
                <div style={{
                  marginTop: "1rem",
                  padding: "0.75rem",
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  borderRadius: "8px",
                  color: "#ef4444",
                  fontSize: "0.875rem"
                }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Success Message - shown after email submission */}
          {step === STEPS.SUCCESS && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
              <div>
            <h1 style={{
              fontSize: "32px",
              marginBottom: "12px",
              fontWeight: "700",
              color: "#000",
              letterSpacing: "-0.02em"
            }}>
              {"🎉 " + t('ultra.researchStarted')}
            </h1>

            <p style={{
              fontSize: "16px",
              lineHeight: "1.5",
              color: "#666",
              marginBottom: "40px"
            }}>
              {t('ultra.interviewingPersonas')}
            </p>

            <div style={{
              padding: "24px",
              backgroundColor: "#f5f5f5",
              borderRadius: "16px",
              marginBottom: "32px",
              border: "1px solid #e5e5e5"
            }}>
              <p style={{
                fontSize: "15px",
                color: "#666",
                marginBottom: "8px",
                fontWeight: "500"
              }}>
                {t('ultra.emailYouAt')}
              </p>
              <p style={{
                fontSize: "17px",
                color: "#000",
                fontWeight: "600"
              }}>
                {email}
              </p>
            </div>

            <button
              onClick={() => navigate(`/ultrafastresearch/${researchId}/progress`)}
              style={{
                width: "100%",
                marginBottom: "16px",
                padding: "20px 28px",
                fontSize: "17px",
                fontWeight: "600",
                border: "none",
                borderRadius: "50px",
                backgroundColor: "#000",
                color: "white",
                cursor: "pointer",
                transition: "all 0.15s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.16)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.12)";
              }}
            >
              {"✨ " + t('ultra.watchMagic')}
            </button>

            <p style={{
              fontSize: "14px",
              color: "#999",
              lineHeight: "1.5"
            }}>
              {t('ultra.closeTabEmail')}
            </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default UltraFastResearch;
